import { ChevronDown, Loader2, SlidersHorizontal, Swords } from "lucide-react";
import type { ReactNode } from "react";
import { Avatar } from "./Avatar";

export type NavbarProps = {
  children?: ReactNode;
  className?: string;
};

export type NavbarActionBarProps = {
  children: ReactNode;
  className?: string;
};

export type QueueButtonProps = {
  isQueuing?: boolean;
  queueTime?: number;
  onClick?: () => void;
  className?: string;
};

export type GameSelectorProps = {
  game: string;
  queue: string;
  playerCount?: string;
  onClick?: () => void;
  className?: string;
};

export type NavbarUserProps = {
  avatar?: ReactNode;
  name: string;
  onClick?: () => void;
  className?: string;
};

function formatTime(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function Navbar({ children, className = "" }: NavbarProps) {
  return (
    <header
      className={`h-14 flex items-center justify-between pr-4 bg-surface-950 relative z-50 shrink-0 ${className}`}
    >
      {children}
    </header>
  );
}

export function NavbarSection({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`flex items-center gap-2 ${className}`}>{children}</div>;
}

export function NavbarActionBar({ children, className = "" }: NavbarActionBarProps) {
  return (
    <div className={`absolute left-1/2 -translate-x-1/2 flex items-center ${className}`}>
      <div className="flex items-center gap-1 rounded-2xl overflow-hidden shadow-lg border border-surface-700/40 bg-surface-800">
        {children}
      </div>
    </div>
  );
}

export function QueueButton({
  isQueuing = false,
  queueTime = 0,
  onClick,
  className = "",
}: QueueButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`
        font-semibold px-5 gap-2 h-9 text-sm transition-all flex items-center
        ${
          isQueuing
            ? "bg-brand-500 text-surface-900 shadow-[0_0_25px_-5px_rgba(38,217,181,0.6)] animate-pulse"
            : "bg-brand-500 hover:bg-brand-400 text-surface-900 shadow-[0_0_20px_-5px_rgba(38,217,181,0.5)]"
        }
        ${className}
      `}
    >
      {isQueuing ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="tabular-nums font-bold">{formatTime(queueTime)}</span>
        </>
      ) : (
        <>
          <Swords className="w-4 h-4" />
          <span className="font-bold">Queue</span>
        </>
      )}
    </button>
  );
}

export function GameSelector({
  game,
  queue,
  playerCount,
  onClick,
  className = "",
}: GameSelectorProps) {
  return (
    <button
      onClick={onClick}
      className={`h-9 px-3 flex items-center gap-2 hover:bg-surface-700/40 transition-all rounded-xl cursor-pointer group ${className}`}
    >
      <SlidersHorizontal className="w-4 h-4 text-brand-500 group-hover:text-brand-400 transition-colors" />
      <div className="hidden sm:flex flex-col items-start">
        <span className="text-xs font-semibold text-white leading-tight">{game}</span>
        <span className="text-[10px] text-surface-400 font-medium leading-tight">
          {queue}
          {playerCount && ` • ${playerCount}`}
        </span>
      </div>
      <ChevronDown className="w-3.5 h-3.5 text-surface-400 group-hover:text-white transition-colors" />
    </button>
  );
}

export function NavbarUser({ avatar, name, onClick, className = "" }: NavbarUserProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 h-9 px-2 rounded-lg hover:bg-surface-800 transition-colors ${className}`}
    >
      {avatar || (
        <Avatar
          size="sm"
          variant="circle"
          initials={name.charAt(0)}
          className="bg-gradient-to-br from-brand-500/30 to-brand-500/10 border border-brand-500/30 !text-brand-500"
        />
      )}
      <span className="hidden sm:inline text-sm font-medium text-white">{name}</span>
      <ChevronDown className="hidden sm:block w-3.5 h-3.5 text-surface-400" />
    </button>
  );
}

// Legacy exports for backwards compatibility
export type NavbarActionProps = {
  icon?: ReactNode;
  label: string;
  variant?: "default" | "primary";
  onClick?: () => void;
};

export type NavbarSelectorProps = {
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  onClick?: () => void;
};

export function NavbarAction({ icon, label, variant = "default", onClick }: NavbarActionProps) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-2 px-5 py-2.5 font-semibold text-sm transition-colors
        ${
          variant === "primary"
            ? "bg-brand-500 text-surface-900 hover:bg-brand-400"
            : "text-surface-300 hover:text-white hover:bg-surface-800"
        }
      `}
    >
      {icon}
      {label}
    </button>
  );
}

export function NavbarSelector({ icon, title, subtitle, onClick }: NavbarSelectorProps) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-surface-800 transition-colors"
    >
      {icon && <span className="text-white">{icon}</span>}
      <div className="text-left">
        <div className="font-semibold text-white">{title}</div>
        {subtitle && <div className="text-xs text-surface-400">{subtitle}</div>}
      </div>
      <ChevronDown className="w-4 h-4 text-surface-400" />
    </button>
  );
}
